import React, { FC, useEffect, useState } from 'react'
import { IconButton, makeStyles, Theme } from '@material-ui/core'
import classnames from 'classnames'

import { RouteTransition } from 'components'
import { EventStages } from 'types'
import { useAppState } from 'providers'
import { ReactComponent as LiveIcon } from 'assets/live-icon.svg'
import { createInteraction } from 'helpers'

import { Video } from 'components/shared'

const PostLiveStreamImg = 'https://d1qlz0eaigbxr5.cloudfront.net/livestream/ps1-post-livestream.png'

interface VodLiveStreamProps {
  startTime: Date
  eventStage?: EventStages
  setPostLiveStream: (changeVal: boolean) => void
}

export const VodLiveStream: FC<VodLiveStreamProps> = ({ startTime, eventStage, setPostLiveStream, ...props }) => {
  const classes = useStyles()

  const {
    appState: { user, showLivestream },
    setShowLivestream
  } = useAppState()

  const videoSrc: string = 'https://d1qlz0eaigbxr5.cloudfront.net/livestream/ps1-livestream.mp4'
  const videoLength: number = 5049000

  const [playTime, setPlayTime] = useState<number>(0)
  const [currentProgress, setCurrentProgress] = useState<number>(0)
  const [joinedTime, setJoinedTime] = useState<number>(0)
  const [videoEnded, setVideoEnded] = useState<boolean>(
    [EventStages.POSTLIVESTREAM, EventStages.POSTEVENT].includes(eventStage as EventStages)
  )

  const sendPlayTime = () => {
    if (playTime > 0) {
      if (joinedTime >= 0) {
        createInteraction(
          'livestream',
          user?.email ?? '',
          'livestream',
          'watchTime',
          eventStage,
          playTime - joinedTime + ''
        )
      }
      setPlayTime(0)
    }
  }

  const generateInteraction = (trigger: string) => {
    if (user?.id) {
      createInteraction('livestream', user?.email || '', 'livestream', trigger, eventStage)
    }
  }

  useEffect(() => {
    generateInteraction('joined')
    //get current playtime
    const offset = new Date().getTime() - startTime.getTime()

    if (offset >= videoLength) {
      setVideoEnded(true)
    } else {
      const offsetTime = offset <= 0 ? 0 : offset / 1000
      setJoinedTime(offsetTime)
      setCurrentProgress(offsetTime)
    }

    if (!showLivestream && [EventStages.LIVESTREAM, EventStages.COUNTDOWN].includes(eventStage as EventStages)) {
      setShowLivestream(true)
    }

    window.addEventListener('beforeunload', sendPlayTime)

    return () => {
      generateInteraction('left')
      window.removeEventListener('beforeunload', sendPlayTime)
    }
    // eslint-disable-next-line
  }, [])

  const [redirectTrigger, setRedirectTrigger] = useState<boolean>(false)

  const exit = () => {
    sendPlayTime()
    if (eventStage && [EventStages.LIVESTREAMENDING, EventStages.POSTLIVESTREAM].includes(eventStage)) {
      setPostLiveStream(true)
    }
    setRedirectTrigger(true)
  }

  let prevTime = 0
  const onTimeUpdate = e => {
    var current = Math.round(e.target.currentTime)
    var time = current / 60
    if ((time % 5 !== 0 && prevTime > 0 && prevTime % 5 === 0) || (prevTime === 84 && time !== 84)) {
      createInteraction(
        'livestream',
        user?.email || '',
        'livestream',
        'viewer',
        eventStage,
        `${prevTime !== 84 ? prevTime : 85}`
      )
    }
    prevTime = time
  }

  return (
    <>
      <div className={classes.root} onContextMenu={e => e.preventDefault()}>
        <div className={classes.streamSideFull}>
          <IconButton
            className={classnames(classes.liveIndicator, !showLivestream && classes.livestreamDisabled)}
            onClick={() => null}
            disableFocusRipple
            disableRipple
            disableTouchRipple
          >
            <LiveIcon width={40} height={30} className={classnames(showLivestream && 'pulse')} />
          </IconButton>
          <IconButton
            className={classes.closeButton}
            onClick={exit}
            disableFocusRipple
            disableRipple
            disableTouchRipple
          >
            Exit
          </IconButton>
          <>
            {!videoEnded && (
              <Video
                videoSrc={videoSrc}
                onEnded={() => {
                  sendPlayTime()
                  setVideoEnded(true)
                }}
                autoPlay={true}
                disableContextMenu={true}
                disableControls={false}
                setPlayTime={setPlayTime}
                seekTo={currentProgress}
                onReady={e => {
                  const player = e.getInternalPlayer()
                  player.setAttribute('controlsList', 'nodownload noremoteplayback nopip')
                  player.setAttribute('disablePictureInPicture', true)
                  if (currentProgress) {
                    setTimeout(() => {
                      e.seekTo(currentProgress)
                    }, 0)
                    setCurrentProgress(0)
                  }
                }}
                hideSeekBar={true}
                onTimeUpdate={onTimeUpdate}
              />
            )}
            {videoEnded && (
              <div className={classes.placeholderImg} style={{ backgroundImage: `url(${PostLiveStreamImg})` }} />
            )}
          </>
        </div>
      </div>
      <RouteTransition animationTrigger={redirectTrigger} route='/event' timeout={300} referrer='stream' />
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'black',
    '& .video-player-wrapper': {
      position: 'unset',
      paddingTop: 0
    }
  },
  closeButton: {
    color: 'white',
    position: 'absolute',
    top: 30,
    right: 30,
    zIndex: 10,
    '& svg': {
      color: '#fff'
    }
  },
  liveIndicator: {
    position: 'absolute',
    top: 30,
    left: 30,
    zIndex: 10,
    '& svg': {
      color: '#fff'
    }
  },
  placeholderImg: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center'
  },
  toastESSButton: {
    height: '24px',
    fontSize: '12px',
    marginBottom: '1rem',
    padding: '0 20px',
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'black',
      color: 'white'
    }
  },
  streamSideWithChat: {
    width: 'calc(100% - 351px)'
  },
  streamSideFull: {
    width: '100%'
  },
  chatSide: {},
  hamburgerMenu: {
    position: 'absolute',
    top: '30px',
    right: '45px',
    display: 'inline-block',
    cursor: 'pointer'
  },
  messagePaper: {
    '&.MuiPaper-root': {
      backgroundColor: 'white !important'
    }
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'white',
    minHeight: '60px',
    maxHeight: '60px'
  },
  mainMenu: {
    height: 'calc(100% - 60px)',
    overflow: 'scroll'
  },
  displayMenu: {
    width: '350px',
    color: 'white',
    height: 'calc(100% - 60px)',
    display: 'flex',
    flexDirection: 'column'
  },
  toolbar: {
    paddingRight: 0,
    paddingLeft: 0,
    alignItems: 'flex-start',
    minHeight: '50px',
    borderBottom: '1px solid #D8DADA'
  },
  tabs: {
    display: 'flex',
    width: '100%',
    color: 'black'
  },
  tab: {
    flex: 1,
    minWidth: '33.33%',
    maxWidth: '100%'
  },
  tabPanel: { flex: 1 },
  peoplePanel: {
    '& .MuiBox-root': {
      paddingTop: 0
    }
  },
  black: {
    background: 'black'
  },
  dialog: {
    position: 'absolute',
    top: 0,
    width: '100%'
  },
  avatarContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    color: 'white',
    alignItems: 'center',
    position: 'relative'
  },
  avatarCircle: {
    width: '100px',
    height: '100px',
    background: 'blue',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9
  },
  avatarLetter: {
    fontSize: '3rem'
  },
  livestreamDisabled: {
    color: '#979797',
    '& svg': {
      '& .icon-bg': {
        fill: '#979797 !important'
      }
    }
  }
}))
